import React, { useEffect, useMemo, useState } from 'react';

export default function RankingPageModal({
  onDone,
  onExit,
  onBackToTierSelection,
  newMovie,
  existingMovies,
  rankingBin,
}) {
  const sortedExisting = useMemo(() => existingMovies || [], [existingMovies]);
  const [steps, setSteps] = useState([{ low: 0, high: sortedExisting.length }]);
  const [rankingNotes, setRankingNotes] = useState('');
  const [hideFromFeed, setHideFromFeed] = useState(false);

  const existingSignature = useMemo(
    () =>
      (existingMovies || [])
        .map((p) => `${p.id}:${p.rankPosition}`)
        .join('|'),
    [existingMovies]
  );

  useEffect(() => {
    setSteps([{ low: 0, high: sortedExisting.length }]);
    setRankingNotes('');
    setHideFromFeed(false);
  }, [existingSignature, sortedExisting.length, newMovie?.selectedMovie?.tmdb_id, rankingBin]);

  const currentStep = steps[steps.length - 1];
  const isComplete = currentStep.low >= currentStep.high;
  const insertionIndex = currentStep.low;
  const currentMid = isComplete
    ? null
    : Math.floor((currentStep.low + currentStep.high) / 2);
  const currentExisting = currentMid === null ? null : sortedExisting[currentMid];

  function pushStep(next) {
    setSteps((prev) => [...prev, next]);
  }

  function handleChooseNewHigher() {
    if (isComplete || currentMid === null) return;
    // New movie is preferred over current mid -> move left
    pushStep({ low: currentStep.low, high: currentMid });
  }

  function handleChooseExistingHigher() {
    if (isComplete || currentMid === null) return;
    // Existing movie preferred -> move right
    pushStep({ low: currentMid + 1, high: currentStep.high });
  }

  function handleSkip() {
    // Treat skip like "existing higher" so search can continue.
    handleChooseExistingHigher();
  }

  function handleBack() {
    if (steps.length > 1) {
      setSteps((prev) => prev.slice(0, -1));
      return;
    }
    if (typeof onBackToTierSelection === 'function') {
      onBackToTierSelection();
    }
  }

  const comparedCount = Math.max(0, steps.length - 1);

  return (
    <div className="flique-modal-backdrop">
      <div className="flique-modal card flique-card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="h5 mb-0">{isComplete ? 'Add notes (optional)' : 'Which did you prefer?'}</h2>
            <button
              type="button"
              className="btn btn-sm btn-outline-light flique-modal-close"
              onClick={onExit}
              aria-label="Close"
            />
          </div>

          {!isComplete && currentExisting ? (
            <>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <button
                    type="button"
                    className="flique-compare-card flique-compare-card-btn p-3 h-100 d-flex flex-column justify-content-between"
                    onClick={handleChooseNewHigher}
                  >
                    <div className="flique-compare-card-head">
                      {newMovie?.selectedMovie?.poster_url ? (
                        <img
                          src={newMovie.selectedMovie.poster_url}
                          alt=""
                          className="flique-compare-card-poster"
                        />
                      ) : (
                        <div
                          className="flique-compare-card-poster flique-compare-card-poster--empty"
                          aria-hidden="true"
                        >
                          —
                        </div>
                      )}
                      <div>
                        <h3 className="h5 mb-1">
                          {newMovie?.selectedMovie?.title || ''}
                          {newMovie?.selectedMovie?.year
                            ? ` (${newMovie.selectedMovie.year})`
                            : ''}
                        </h3>
                        {newMovie?.selectedMovie?.director ? (
                          <p className="small mb-0 text-muted">
                            {newMovie.selectedMovie.director}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </button>
                </div>

                <div className="col-md-6">
                  <button
                    type="button"
                    className="flique-compare-card flique-compare-card-btn p-3 h-100 d-flex flex-column justify-content-between"
                    onClick={handleChooseExistingHigher}
                  >
                    <div className="flique-compare-card-head">
                      {currentExisting.movie?.poster_url ? (
                        <img
                          src={currentExisting.movie.poster_url}
                          alt=""
                          className="flique-compare-card-poster"
                        />
                      ) : (
                        <div
                          className="flique-compare-card-poster flique-compare-card-poster--empty"
                          aria-hidden="true"
                        >
                          —
                        </div>
                      )}
                      <div>
                        <h3 className="h5 mb-1">
                          {currentExisting.movie?.title || ''}
                          {currentExisting.movie?.year
                            ? ` (${currentExisting.movie.year})`
                            : ''}
                        </h3>
                        {currentExisting.movie?.director ? (
                          <p className="small mb-0 text-muted">
                            {currentExisting.movie.director}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </>
          ) : null}

          {isComplete && (
            <div className="mb-3">
              <textarea
                id="flique-ranking-notes"
                className="form-control mb-2"
                rows={3}
                placeholder="Anything you want to remember about this watch…"
                value={rankingNotes}
                onChange={(e) => setRankingNotes(e.target.value)}
              />
              <div className="form-check">
                <input
                  id="flique-hide-from-feed"
                  type="checkbox"
                  className="form-check-input"
                  checked={hideFromFeed}
                  onChange={(e) => setHideFromFeed(e.target.checked)}
                />
                <label className="form-check-label small text-muted" htmlFor="flique-hide-from-feed">
                  Hide from friends' feed
                </label>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={handleBack}
                disabled={steps.length <= 1 && typeof onBackToTierSelection !== 'function'}
              >
                Back
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={handleSkip}
                disabled={isComplete}
              >
                Skip
              </button>
            </div>

            {isComplete ? (
              <button
                type="button"
                className="btn btn-sm btn-flique"
                onClick={() =>
                  onDone({
                    insertionIndex,
                    comparedCount,
                    notes: rankingNotes.trim() || undefined,
                    hideFromFeed,
                  })
                }
              >
                Finish
              </button>
            ) : (
              <span aria-hidden="true" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
